import React from 'react';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, Warning, Error, Refresh } from '@mui/icons-material';
import { SERVICE_STATUS } from '../data/apiConfig';

const Status = () => {
    const services = [
        {
            name: 'Accounts API',
            status: SERVICE_STATUS.accounts.status,
            description: 'Service d\'authentification OAuth2',
            lastChecked: SERVICE_STATUS.accounts.lastChecked
        },
        {
            name: 'Transactions API',
            status: SERVICE_STATUS.api.status,
            description: 'Service de gestion des transactions',
            lastChecked: SERVICE_STATUS.api.lastChecked
        }
    ];

    const getStatusConfig = (status) => {
        switch (status) {
            case 'operational':
                return {
                    icon: <CheckCircle className="text-green-600" />,
                    color: 'text-green-600',
                    bgColor: 'bg-green-100',
                    label: 'Opérationnel'
                };
            case 'degraded':
                return {
                    icon: <Warning className="text-yellow-600" />,
                    color: 'text-yellow-600',
                    bgColor: 'bg-yellow-100',
                    label: 'Dégradé'
                };
            case 'maintenance':
                return {
                    icon: <Error className="text-red-600" />,
                    color: 'text-red-600',
                    bgColor: 'bg-red-100',
                    label: 'Maintenance'
                };
            default:
                return {
                    icon: <Warning className="text-gray-600" />,
                    color: 'text-gray-600',
                    bgColor: 'bg-gray-100',
                    label: 'Inconnu'
                };
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <>
            <Helmet>
                <title>Statut des Services - Netikash Developer Portal</title>
                <meta name="description" content="Statut en temps réel des services API Netikash. Surveillez l'état opérationnel de tous les endpoints." />
            </Helmet>
            <div className="min-h-screen bg-gray-50 dark:bg-slate-950 dark:text-slate-100 pt-16">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">Statut des Services</h1>
                    <p className="text-gray-600">
                        Surveillez l'état de tous les services Netikash en temps réel.
                    </p>
                </div>

                {/* Services Status */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-8">
                    <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                        <h2 className="text-xl font-semibold text-gray-900">Services API</h2>
                        <button className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-900">
                            <Refresh fontSize="small" />
                            <span>Actualiser</span>
                        </button>
                    </div>
                    <div className="divide-y divide-gray-200">
                        {services.map((service, index) => {
                            const config = getStatusConfig(service.status);
                            return (
                                <div key={index} className="p-6 hover:bg-gray-50 transition-colors">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-start space-x-4 flex-1">
                                            <div className={`p-3 rounded-xl ${config.bgColor}`}>
                                                {config.icon}
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                                    {service.name}
                                                </h3>
                                                <p className="text-sm text-gray-600 mb-2">
                                                    {service.description}
                                                </p>
                                                <div className="flex items-center space-x-4 text-xs text-gray-500">
                                                    <span>Dernière vérification: {formatDate(service.lastChecked)}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="ml-4">
                                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.color} ${config.bgColor}`}>
                                                {config.label}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Historique (placeholder) */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Historique des incidents</h2>
                    <div className="text-center py-8 text-gray-500">
                        <p className="text-sm">Aucun incident récent</p>
                        <p className="text-xs mt-2">Tous les services fonctionnent normalement</p>
                    </div>
                </div>
            </div>
            </div>
        </>
    );
};

export default Status;
